/**
 * Homepage Content for StarNexus platform
 * Portal service catalog layout
 * @module components/starnexus/home/HomepageContent
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@amzn/awsui-components-console/icon';
import Box from '@amzn/awsui-components-console/box';
import Grid from '@amzn/awsui-components-console/grid';
import Container from '@amzn/awsui-components-console/container';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import Header from '@amzn/awsui-components-console/header';
import Link from '@amzn/awsui-components-console/link';
import Cards from '@amzn/awsui-components-console/cards';
import Badge from '@amzn/awsui-components-console/badge';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';

import './styles.css';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../utils/apiClient';

interface ServiceItem {
  name: string;
  status: 'Active' | 'Coming soon';
  description: string;
  href: string;
  icon: string;
}

const services: ServiceItem[] = [
  { name: 'StarUploader', status: 'Active', description: 'Upload a file, let AI detect the schema, and publish it to Andes.', href: '/starnexus/uploader/datasets', icon: 'upload' },
  { name: 'StarBridge', status: 'Coming soon', description: 'Ingest and transform third-party inbound data.', href: '#', icon: 'share' },
  { name: 'StarMailer', status: 'Coming soon', description: 'Auto-extract data from carrier report email attachments.', href: '#', icon: 'envelope' },
  { name: 'StarSwap', status: 'Coming soon', description: 'Share datasets across teams with access controls.', href: '#', icon: 'file' },
];

interface ActivityRow {
  owner: string;
  action: string;
  target: string;
  href?: string;
  time: string;
}

/** Format ISO date string as relative time (e.g. "2 hours ago"). */
function formatRelativeTime(iso: string): string {
  if (!iso) return '';
  const delta = Date.now() - new Date(iso).getTime();
  if (isNaN(delta) || delta < 0) return 'just now';
  const mins = Math.floor(delta / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins > 1 ? 's' : ''} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  return new Date(iso).toLocaleDateString();
}

export const HomepageContent: React.FC = () => {
  const navigate = useNavigate();
  const [recentActivity, setRecentActivity] = useState<ActivityRow[]>([]);
  const [stats, setStats] = useState<Array<{ label: string; value: string }>>([
    { label: 'Active datasets', value: '-' },
    { label: 'Draft datasets', value: '-' },
    { label: 'Teams', value: '-' },
    { label: 'Last activity', value: '-' },
  ]);

  // Load recent activity + stats from DDB. Stats derived from the same dataset list.
  // Full audit log is a future enhancement; the "recent activity" here is a proxy
  // derived from the dataset list sorted by updatedAt.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch(apiUrl('/api/datasets'), { ...apiFetchDefaults }).then(r => r.json()).catch(() => ({ datasets: [] })),
      apiFetch(apiUrl('/api/team/teams'), { ...apiFetchDefaults }).then(r => r.json()).catch(() => ({ teams: [] })),
    ]).then(([dsData, teamData]) => {
      if (cancelled) return;
      const items: any[] = Array.isArray(dsData.items) ? dsData.items : Array.isArray(dsData.datasets) ? dsData.datasets : [];
      const teams: any[] = Array.isArray(teamData.teams) ? teamData.teams : [];

      // Recent activity: top 5 datasets by updatedAt (updates ranked above creates).
      const sorted = [...items].sort((a, b) => {
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return tb - ta;
      }).slice(0, 5);
      const rows: ActivityRow[] = sorted.map((d) => ({
        owner: d.owner || d.createdBy || 'unknown',
        action: d.updatedAt && d.updatedAt !== d.createdAt ? 'Updated dataset' : 'Created dataset',
        target: d.datasetName || d.name || d.datasetId || '(unnamed)',
        href: d.datasetId ? `/starnexus/uploader/datasets/${d.datasetId}` : undefined,
        time: formatRelativeTime(d.updatedAt || d.createdAt || ''),
      }));
      setRecentActivity(rows);

      // Stats: derive from same payload.
      const activeCount = items.filter((d: any) => d.status === 'active').length;
      const draftCount = items.filter((d: any) => d.status === 'draft').length;
      const lastActivity = items
        .map((d: any) => d.updatedAt || d.createdAt)
        .filter(Boolean)
        .sort()
        .pop();
      setStats([
        { label: 'Active datasets', value: String(activeCount) },
        { label: 'Draft datasets', value: String(draftCount) },
        { label: 'Teams', value: String(teams.length) },
        { label: 'Last activity', value: lastActivity ? formatRelativeTime(lastActivity) : '-' },
      ]);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <Box margin={{ bottom: 'l' }}>
      {/* Hero Header Section */}
      <div className="custom-home__header">
        <Box padding={{ vertical: 'xxxl', horizontal: 's' }}>
          <Grid
            disableGutters
            gridDefinition={[
              { offset: { l: 2, xxs: 1 }, colspan: { l: 8, xxs: 10 } },
              { colspan: { l: 5, s: 6, xxs: 10 }, offset: { l: 2, xxs: 1 } },
              { colspan: { l: 3, s: 4, xxs: 10 }, offset: { s: 0, xxs: 1 } }
            ]}
          >
            <Box padding={{ top: 'xs' }} fontWeight="light">
              <span className="custom-home-text-inverted">Data Processing &amp; Integration</span>
            </Box>
            <div className="custom-home-text-inverted">
              <Box variant="h1" fontWeight="bold" padding="n" fontSize="display-l" color="inherit">
                StarNexus
              </Box>
              <Box fontWeight="light" padding={{ bottom: 's' }} fontSize="display-l" color="inherit">
                Star Data Services Portal
              </Box>
              <Box variant="p" fontWeight="light">
                <span className="custom-home-text-secondary">
                  IVT Starcatcher's hub for Star data services. Upload a file, publish it to Andes, manage access, and monitor pipelines, with AI-assisted setup.
                </span>
              </Box>
            </div>
            <Container>
              <SpaceBetween size="s">
                <Box variant="h2" padding="n">
                  Get Started
                </Box>
                <Box variant="p">Create your first dataset with AI-assisted configuration in minutes.</Box>
                <Button variant="primary" onClick={() => navigate('/starnexus/uploader/datasets/create')}>
                  Create dataset
                </Button>
              </SpaceBetween>
            </Container>
          </Grid>
        </Box>
      </div>

      {/* Main Content Section */}
      <Box padding={{ top: 'xxxl', horizontal: 's', bottom: 'l' }}>
        <Grid
          disableGutters
          gridDefinition={[
            {
              colspan: { xl: 6, l: 5, s: 6, xxs: 10 },
              offset: { l: 2, xxs: 1 }
            },
            {
              colspan: { xl: 2, l: 3, s: 4, xxs: 10 },
              offset: { s: 0, xxs: 1 }
            }
          ]}
        >
          <SpaceBetween size="xxl">
            {/* Quick Stats Row */}
            <ColumnLayout columns={4} variant="text-grid">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <Box variant="awsui-key-label">{stat.label}</Box>
                  <Box fontSize="heading-l" fontWeight="bold">{stat.value}</Box>
                </div>
              ))}
            </ColumnLayout>

            {/* Service Catalog */}
            <div>
              <div className="homepage-section-header">
                <Header variant="h1" headingTagOverride="h2">
                  Service Catalog
                </Header>
              </div>
              <Cards
                items={services}
                cardDefinition={{
                  header: (item) =>
                    item.status === 'Active' ? (
                      <Link fontSize="heading-m" onFollow={(e) => { e.preventDefault(); navigate(item.href); }}>
                        <Icon name={item.icon as any} /> {item.name}
                      </Link>
                    ) : (
                      <span><Icon name={item.icon as any} /> {item.name}</span>
                    ),
                  sections: [
                    {
                      id: 'status',
                      content: (item) => (
                        <Badge color={item.status === 'Active' ? 'green' : 'grey'}>{item.status}</Badge>
                      ),
                    },
                    {
                      id: 'description',
                      content: (item) => item.description,
                    },
                  ],
                }}
                cardsPerRow={[{ cards: 1 }, { minWidth: 400, cards: 2 }]}
              />
            </div>

            {/* Recent Activity (derived from DDB DatasetsTable, sorted by updatedAt) */}
            <Container header={<Header variant="h2">Recent Activity</Header>}>
              {recentActivity.length === 0 ? (
                <Box color="text-body-secondary" textAlign="center" padding="s">
                  No activity yet. Create your first dataset to see it here.
                </Box>
              ) : (
                <SpaceBetween size="xs">
                  {recentActivity.map((item, idx) => (
                    <Box key={idx} padding={{ vertical: 'xxs' }}>
                      <Box variant="span" fontWeight="bold">{item.owner}</Box>{' '}
                      {item.action}{' '}
                      {item.href ? (
                        <Link variant="primary" onFollow={(e) => { e.preventDefault(); item.href && navigate(item.href); }}>
                          {item.target}
                        </Link>
                      ) : (
                        <Box variant="span">{item.target}</Box>
                      )}{' '}
                      <Box variant="span" color="text-status-inactive">{item.time}</Box>
                    </Box>
                  ))}
                </SpaceBetween>
              )}
            </Container>
          </SpaceBetween>

          {/* Sidebar */}
          <SpaceBetween size="xxl" className="custom-home__sidebar">
            {/* Quick actions */}
            <Container
              header={
                <Header variant="h2">
                  Quick Actions <Icon name="external" size="inherit" />
                </Header>
              }
            >
              <ul aria-label="Quick action links" className="custom-list-separator">
                <li>
                  <Link onFollow={(e) => { e.preventDefault(); navigate('/starnexus/uploader/datasets/create'); }}>
                    Create new dataset
                  </Link>
                </li>
                <li>
                  <Link onFollow={(e) => { e.preventDefault(); navigate('/starnexus/uploader/processing'); }}>
                    View processing queue
                  </Link>
                </li>
                <li>
                  <Link onFollow={(e) => { e.preventDefault(); navigate('/starnexus/uploader/datasets'); }}>
                    Manage datasets
                  </Link>
                </li>
                <li>
                  <Link onFollow={(e) => { e.preventDefault(); navigate('/starnexus/team'); }}>
                    Team management
                  </Link>
                </li>
              </ul>
            </Container>

            {/* Documentation */}
            <Container
              header={
                <Header variant="h2">
                  Documentation <Icon name="external" size="inherit" />
                </Header>
              }
            >
              <ul aria-label="Documentation links" className="custom-list-separator">
                <li>
                  <Link onFollow={(e) => { e.preventDefault(); navigate('/starnexus/docs/getting-started'); }}>
                    Getting started guide
                  </Link>
                </li>
                <li>
                  <Link onFollow={(e) => { e.preventDefault(); navigate('/starnexus/docs/schema-config'); }}>
                    Schema configuration
                  </Link>
                </li>
                <li>
                  <Link onFollow={(e) => { e.preventDefault(); navigate('/starnexus/docs/sql-reference'); }}>
                    SQL transformation reference
                  </Link>
                </li>
                <li>
                  <Link onFollow={(e) => { e.preventDefault(); navigate('/starnexus/docs/andes-integration'); }}>
                    Andes integration
                  </Link>
                </li>
              </ul>
            </Container>
          </SpaceBetween>
        </Grid>
      </Box>
    </Box>
  );
};

export default HomepageContent;
